/**
 * CreditPackageCard — карточка пакета кредитов на /credits.
 *
 * Figma source: `319:4731` (Credits screen) — близнец `319:4631` (Plans),
 * та же radio-selection модель (см. PlanCard.jsx). Карточка целиком
 * выступает кнопкой выбора пакета; per-card «Купить» нет — покупка идёт
 * через единый CTA внизу экрана (Figma 319:4829).
 *
 * Props:
 *   pack         — { id, credits, price, oldPrice, discount, isBestChoice|popular }
 *   method       — 'card' | 'crypto' | 'sbp' | 'stars'  (выбранный метод оплаты)
 *   isSelected   — boolean — оранжевый filled radio + 1px orange border
 *   onSelect     — fn(pack) — тап по карточке (pure selection, без побочных эффектов)
 *
 * Layout (Figma + параллельный PlanCard.jsx):
 *   Glass card: rounded-home-24, bg-glass-8, border (default → border-default, selected → orange)
 *   Row 1: radio 24×24 (top-left)
 *   Row 2: [credits + кредитов + green discount chip] | [oldPrice strike + price]
 *   Row 3: per-credit subtitle
 *
 * «Лучший выбор» — angle badge в правом-верхнем углу, рендерится по флагу
 *   isBestChoice (backend шлёт `popular: bool`, нормализуем).
 *
 * Цвет discount-чипа — figma `#22C55E` (text-success), та же зелёная схема,
 * что в PlanCard (bg `text-success/15` + text `text-success`).
 */
import { useTranslation } from 'react-i18next';

const USD_TO_STARS = 50;
const USD_TO_RUB = 100;

function formatThousands(n) {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function formatPerCredit(price, credits) {
  const val = price / credits;
  return val.toFixed(3).replace(/\.?0+$/, '');
}

function formatPrice(pack, method) {
  if (method === 'stars') {
    const stars = Math.ceil(pack.price * USD_TO_STARS);
    return { main: `${formatThousands(stars)} ⭐`, old: null };
  }
  if (method === 'sbp') {
    return { main: `${formatThousands(Math.round(pack.price * USD_TO_RUB))} ₽`, old: null };
  }
  return {
    main: `$${pack.price}`,
    old: pack.oldPrice ? `$${pack.oldPrice}` : null,
  };
}

function formatPerCreditDisplay(pack, method, perCreditText) {
  if (method === 'stars') {
    const stars = Math.ceil(pack.price * USD_TO_STARS);
    const perCredit = (stars / pack.credits).toFixed(1);
    return `${perCredit} ⭐ / ${perCreditText}`;
  }
  if (method === 'sbp') {
    const rubles = Math.round(pack.price * USD_TO_RUB);
    return `${formatPerCredit(rubles, pack.credits)} ₽ / ${perCreditText}`;
  }
  return `$${formatPerCredit(pack.price, pack.credits)} / ${perCreditText}`;
}

export default function CreditPackageCard({
  pack,
  method = 'card',
  isSelected = false,
  onSelect,
}) {
  const { t } = useTranslation();
  const isBestChoice = pack.isBestChoice ?? pack.popular ?? false;
  const discountPct =
    typeof pack.discount === 'string'
      ? Math.abs(parseInt(pack.discount, 10)) || 0
      : (pack.discount ?? 0);

  const { main: priceMain, old: priceOld } = formatPrice(pack, method);
  const perCreditLabel = formatPerCreditDisplay(pack, method, t('credits.perCredit'));

  const handleActivate = () => onSelect?.(pack);
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.(pack);
    }
  };

  // Radio circle — те же state'ы, что в PlanCard (без isCurrent — для credits
  // понятия «текущий пакет» нет):
  //   isSelected → толстое оранжевое кольцо (border-8 inside)
  //   default    → серая обводка (border-2)
  const radioBase = 'w-6 h-6 shrink-0 rounded-full';
  const radioClass = isSelected
    ? `${radioBase} border-8 border-accent-orange-start`
    : `${radioBase} border-2 border-glass-16`;

  const surfaceClasses = [
    'relative rounded-home-24 overflow-hidden',
    'bg-glass-8',
    isSelected ? 'border border-accent-orange-start' : 'border border-border-default',
    'cursor-pointer',
  ].join(' ');

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      className={surfaceClasses}
      data-testid="credit-package-card"
      data-selected={isSelected ? 'true' : 'false'}
    >
      {/* «Лучший выбор» badge — top-right corner-attached (slider/tag 276:1930):
          bottom-left corner radius 16 (mirrors PlanCard:159), other corners 0. */}
      {isBestChoice && (
        <div className="absolute top-0 right-0 z-10">
          <span
            data-testid={`credit-best-choice-${pack.id}`}
            className="block bg-gradient-to-br from-accent-orange-start to-accent-orange-end text-text-inverse font-bold text-badge px-3 py-1 rounded-bl-home-16"
          >
            {t('common.bestChoice')}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-3 p-6">
        {/* Row 1 — radio */}
        <div className="flex items-center">
          <span
            aria-hidden="true"
            data-testid={`credit-radio-${pack.id}`}
            className={radioClass}
          />
        </div>

        {/* Row 2 — credits info | price */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-sans font-bold text-[20px] leading-none text-text-primary">
                {pack.credits}
              </span>
              <span className="font-sans font-bold text-[14px] leading-none text-text-secondary">
                {t('common.credits')}
              </span>
              {discountPct > 0 && (
                <span
                  data-testid="discount-badge"
                  className="font-sans font-bold text-badge text-text-success bg-text-success/15 px-2 py-0.5 rounded-full whitespace-nowrap"
                >
                  -{discountPct}%
                </span>
              )}
            </div>
            <span
              data-testid={`per-credit-${pack.id}`}
              className="font-sans text-badge text-text-secondary"
            >
              {perCreditLabel}
            </span>
          </div>

          <div className="flex flex-col items-end gap-0.5 shrink-0">
            {priceOld && (
              <span
                data-testid={`old-price-${pack.id}`}
                className="font-sans text-plan text-text-tertiary line-through"
              >
                {priceOld}
              </span>
            )}
            <span className="font-sans font-bold text-[20px] leading-none text-text-primary">
              {priceMain}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
